import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Gift, ChevronDown, ArrowRightLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface DashboardSwitcherProps {
  currentDashboard: "professional" | "customer";
  userEmail: string;
  className?: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function DashboardSwitcher({ 
  currentDashboard, 
  userEmail,
  className 
}: DashboardSwitcherProps) {
  const navigate = useNavigate();
  const [hasCustomerProfile, setHasCustomerProfile] = useState(false);
  const [hasProfessionalAccount, setHasProfessionalAccount] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkDualRoles();
  }, [userEmail]);

  const checkDualRoles = async () => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    const emailLower = userEmail.toLowerCase();
    setLoading(true);

    try {
      // Check if user has a professional merchant account by email
      // Query merchants where contact_person_email or business_email matches (case-insensitive)
      const { data: contactEmailMerchants } = await supabase
        .from("merchants")
        .select("id, platform_type, shopify_store_domain, contact_person_email, business_email")
        .ilike("contact_person_email", emailLower)
        .limit(5);
      
      const { data: businessEmailMerchants } = await supabase
        .from("merchants")
        .select("id, platform_type, shopify_store_domain, contact_person_email, business_email")
        .ilike("business_email", emailLower)
        .limit(5);
      
      // Combine results
      const allMerchants = [...(contactEmailMerchants || []), ...(businessEmailMerchants || [])];
      // Remove duplicates by id
      const merchants = allMerchants.filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i);
      
      console.log("DashboardSwitcher: checking email", emailLower, "merchants found:", merchants);

      if (merchants && merchants.length > 0) {
        // Find the professional merchant
        const professionalMerchant = merchants.find(m => {
          const isProfessional = m.platform_type === "professional" || 
            (m.platform_type === "standalone" && m.shopify_store_domain?.includes(".professional."));
          return isProfessional;
        });
        
        console.log("DashboardSwitcher: professional merchant found:", professionalMerchant);
        setHasProfessionalAccount(!!professionalMerchant);
      } else {
        // Fallback: Also check via authenticated session if available
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const { data: merchantUser } = await supabase
            .from("merchant_users")
            .select("merchant_id")
            .eq("user_id", session.user.id)
            .limit(1);

          if (merchantUser && merchantUser.length > 0) {
            const { data: merchant } = await supabase
              .from("merchants")
              .select("platform_type, shopify_store_domain")
              .eq("id", merchantUser[0].merchant_id)
              .single();

            const isProfessional = 
              merchant?.platform_type === "standalone" && 
              merchant?.shopify_store_domain?.includes(".professional.");
            const isProfessionalPlatform = merchant?.platform_type === "professional";
            
            setHasProfessionalAccount(isProfessional || isProfessionalPlatform);
          }
        }
      }

      // Check if user has customer activity (participated in events/purchases)
      const [giftCheck, purchaseCheck, inviteeCheck, purchaseParticipantCheck] = await Promise.all([
        // Check if organizer of any gift event
        supabase
          .from("gift_reminders")
          .select("id")
          .eq("customer_email", emailLower)
          .limit(1),
        // Check if organizer of any group purchase
        supabase
          .from("group_purchases")
          .select("id")
          .eq("organizer_email", emailLower)
          .limit(1),
        // Check if invited to any event
        supabase
          .from("event_invitees")
          .select("id")
          .eq("email", emailLower)
          .limit(1),
        // Check if participant in any group purchase
        supabase
          .from("group_purchase_participants")
          .select("id")
          .eq("email", emailLower)
          .limit(1),
      ]);

      const hasCustomer = 
        (giftCheck.data && giftCheck.data.length > 0) ||
        (purchaseCheck.data && purchaseCheck.data.length > 0) ||
        (inviteeCheck.data && inviteeCheck.data.length > 0) ||
        (purchaseParticipantCheck.data && purchaseParticipantCheck.data.length > 0);
      
      setHasCustomerProfile(hasCustomer);
    } catch (error) {
      console.error("Error checking dual roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = (target: "professional" | "customer") => {
    if (target === "professional") {
      navigate("/merchant-dashboard");
    } else {
      // Set session for customer portal and navigate to main dashboard
      const SESSION_KEY = "shakatah_customer_session";
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ 
        email: userEmail, 
        verified: true 
      }));
      navigate("/my-purchases");
    }
  };

  // Show switcher if:
  // - On professional dashboard (always show to access customer portal)
  // - On customer dashboard AND user has a professional account
  if (loading) return null;
  
  // Hide switcher on customer portal if user doesn't have a professional account
  if (currentDashboard === "customer" && !hasProfessionalAccount) {
    return null;
  }

  const otherDashboard = currentDashboard === "professional" ? "customer" : "professional";


  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={cn(
            "gap-2",
            currentDashboard === "professional" 
              ? "border-cyan-500/30 text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950/30"
              : "border-customer/30 text-customer hover:bg-customer/10",
            className
          )}
        >
          <ArrowRightLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Switch Dashboard</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          You have access to multiple dashboards
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Current dashboard - disabled */}
        <DropdownMenuItem disabled className="opacity-60">
          {currentDashboard === "professional" ? (
            <>
              <Briefcase className="h-4 w-4 mr-2 text-cyan-600" />
              <span>Professional Dashboard</span>
              <Badge variant="outline" className="ml-auto text-xs">Current</Badge>
            </>
          ) : (
            <>
              <Gift className="h-4 w-4 mr-2 text-customer" />
              <span>Customer Portal</span>
              <Badge variant="outline" className="ml-auto text-xs">Current</Badge>
            </>
          )}
        </DropdownMenuItem>

        {/* Other dashboard - clickable */}
        <DropdownMenuItem 
          onClick={() => handleSwitch(otherDashboard)}
          className="cursor-pointer"
        >
          {otherDashboard === "professional" ? (
            <>
              <Briefcase className="h-4 w-4 mr-2 text-cyan-600" />
              <span>Professional Dashboard</span>
            </>
          ) : (
            <>
              <Gift className="h-4 w-4 mr-2 text-customer" />
              <span>Customer Portal</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
